import React, { createContext, useContext, useState } from "react";
import { useEffect } from "react";
import { callMsGraph } from "../auth/graph";
import { useMsal } from "@azure/msal-react";
import { jwtDecode } from "jwt-decode";

import { loginRequest } from "../auth/authConfig";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import axios from "axios";
import { useQuery } from "react-query";
import SessionValidation from "./SessionValidationContext";
import { postmortemApiEndpoint } from "../config";
import { useFetchWithRetry } from "../Utils/fetchWithRetry";
import { getConsumerKey } from "../services/maerskApiTokenService";
const UserAccessContext = createContext({});
export const UserAccessContextProvider = ({ children }) => {
  const { instance, accounts } = useMsal();
  const { fetchWithRetry } = useFetchWithRetry();
  const [graphData, setGraphData] = useState(null);
  const [token, setToken] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [userAccessData, setUserAccessData] = useState(null);
  const [userIsAdmin, setUserIsAdmin] = useState(null);
  const [userIsMttsAdmin, setUserIsMttsAdmin] = useState(null);
  const [refetchAccess, setRefetchAccess] = useState(false);
  const [fetchAgain, setFetchAgain] = useState(false);
  const [userIsSreUser, setUserIsSreUser] = useState(null);
  const [userIsAMimMember, setUserIsAMimMember] = useState(null);
  const [userIsApmtMember, setUserIsApmtMember] = useState(null);

  // "APMT"

  const { setSessionExpired } = SessionValidation();

  const fetchToken = async () => {
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });



    const decodedToken = jwtDecode(tokenResponse.idToken);
    const currentTime = Math.floor(Date.now() / 1000); // current time in seconds


    if (decodedToken.exp <= currentTime) {
      throw new Error("Access token is expired");
  
    }

      return tokenResponse;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        sessionStorage.clear();
        setSessionExpired(true);
      }
      throw error;
    }
  };

  const {
    data: tokenResponse,
    isLoading: isTokenLoading,
    error: tokenError,
  } = useQuery("tokenInUserAccessContext", fetchToken, {
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      // setAccessToken(data.accessToken);
    },
    // refetchOnWindowFocus: false,
  });

  async function fetchProfileData(tokenResponse) {
    try {
      const graphResponse = await callMsGraph(tokenResponse.accessToken);
      return graphResponse;
    } catch (error) {
      throw error;
    }
  }

  async function fetchUserAccess(tokenResponse, userEmail) {
    try {
      const apiEndpoint = postmortemApiEndpoint;
      const url = `${apiEndpoint}/teams/email/${userEmail}`;
      const response = await fetchWithRetry(url, 'USER_ACCESS_DATA', 3, 1000);

      if (response && (response.length > 0 || Array.isArray(response))) {
        return response;
      } else {
        throw new Error(`Unexpected response: ${JSON.stringify(response)}`);
      }
    } catch (error) {
      console.error(error);
      // sessionStorage.clear();
      throw error;
    }
  }

  async function checkMttsAdminStatus(tokenResponse, userEmail) {
    try {
      const apiEndpoint = postmortemApiEndpoint;
      const url = `${apiEndpoint}/mtts/check-admin?userEmail=${encodeURIComponent(userEmail)}`;
      // Gateway (api-cdt.maersk.com) requires both the bearer token and consumer-key, same as every other call here.
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${tokenResponse.idToken}`,
          "consumer-key": getConsumerKey(),
          "X-User": userEmail,
        },
      });
      return response.data?.isAdmin === true;
    } catch (error) {
      console.error("Error checking MTTS admin status:", error);
      // Fail-secure: treat as not admin if the check fails
      return false;
    }
  }

  const {
    data: userAccess,
    isError: userAccessError,
    isLoading: userAccessLoading,
    refetch: refetchUserAccess,
  } = useQuery(
    ["userAccess", accounts[0]?.username, refetchAccess],

    () => fetchUserAccess(tokenResponse, accounts[0]?.username),
    {
      onSuccess: (data) => {
        setGraphData(accounts[0]);
        setUserIsAdmin(
          data.some(
            (obj) => obj.postMortemId === "SRE" || obj.postMortemId === "LT"
          )
        );


        setUserIsAMimMember(data.some((obj) => obj.postMortemId === "MIMTeam"));

        // if()

        setUserIsSreUser(data.some((obj) => obj.postMortemId === "SRE"));

        setUserIsApmtMember(data.some((obj) => obj.postMortemId === "APMT"));
        setUserAccessData(data);
      },
      enabled: !!tokenResponse,
      refetchOnWindowFocus: false,
      cacheTime: 0,

      retry: (failureCount, error) => {
        if (failureCount > 3) return false;
        if (error.response && [401].includes(error.response.status)) {
          let forgeRockErrorCode = error.response?.data?.errorcode;
          if (
            forgeRockErrorCode &&
            forgeRockErrorCode === "steps.jwt.TokenExpired"
          ) {
            console.error(forgeRockErrorCode);
          }
          sessionStorage.clear();
          // setSessionExpired(true);
          return true;
        } else {
          return false;
        }
      },
      retryDelay: (attemptIndex) => Math.min(attemptIndex * 1000, 3000),
      // cacheTime:0,
    }
  );

  useEffect(() => {
    if (tokenResponse) {
      refetchUserAccess();
    }
  }, [tokenResponse, refetchUserAccess, fetchAgain]);

  // Re-validated periodically per session so MTTS team changes take effect
  // without requiring the user to fully log out.
  const MTTS_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

  useEffect(() => {
    if (!tokenResponse || !accounts[0]?.username) return;

    const userEmail = accounts[0].username;
    const cacheKey = `mtts_admin_${userEmail}`;
    let cachedEntry = null;
    try {
      const cachedRaw = sessionStorage.getItem(cacheKey);
      cachedEntry = cachedRaw ? JSON.parse(cachedRaw) : null;
    } catch {
      cachedEntry = null;
    }

    const isCacheValid =
      cachedEntry &&
      typeof cachedEntry.isAdmin === "boolean" &&
      Date.now() - cachedEntry.timestamp < MTTS_CACHE_TTL_MS;

    if (isCacheValid) {
      setUserIsMttsAdmin(cachedEntry.isAdmin);
      return;
    }

    checkMttsAdminStatus(tokenResponse, userEmail)
      .then((isAdmin) => {
        setUserIsMttsAdmin(isAdmin);
        sessionStorage.setItem(cacheKey, JSON.stringify({ isAdmin, timestamp: Date.now() }));
      })
      .catch(() => {
        setUserIsMttsAdmin(false);
        sessionStorage.setItem(cacheKey, JSON.stringify({ isAdmin: false, timestamp: Date.now() }));
      });
  }, [tokenResponse, accounts[0]?.username]);

  return (
    <UserAccessContext.Provider
      value={{
        graphData,
        token,
        setToken,
        accessToken,
        userIsAdmin,
        userIsMttsAdmin,
        userAccessData,
        userAccessLoading,

        userAccessError,

        setRefetchAccess,
        setFetchAgain,
        userIsSreUser,
        userIsAMimMember,
        userIsApmtMember,
      }}
    >
      {children}
    </UserAccessContext.Provider>
  );
};

function UserInfo() {
  return useContext(UserAccessContext);
}
UserInfo.whyDidYouRender = true;

export default UserInfo;
